import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@compiled/react';
import Markdown from 'markdown-to-jsx';
import { useIntl, defineMessages } from 'react-intl-next';

import { Anchor, Box, Text, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';

import type { CommentAction } from '@confluence/inline-comments-common/entry-points/inlineCommentsTypes';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter';
import { getEditorAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getEditorAnnotationEventEmitter';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import type { CommentData } from '@confluence/comments-data/entry-points/useCommentsData';
import { usePageClass } from '@confluence/comments-hooks/entry-points/usePageClass';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { CommentBatchList } from '@confluence/comment/entry-points/CommentBatchList';
import { getSortedCommentThreads } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper';
import {
	useCommentsPanelActions,
	useCommentsPanelState,
	SortValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { SELECT_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { selectInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience';
import { fg } from '@confluence/feature-gating';
import { useCommentsDataState } from '@confluence/comments-data/entry-points/useCommentsData';

import { getCommentThreadsToBatch } from '../helper/commentThreadHelper';
import { mapCommentSummaryResponseToMarkdown } from '../data/mapCommentSummaryResponseToMarkdown';
import type { CommentSummaryResponse } from '../data/commentSummaryTypes';
import exampleResponse from '../data/commentSummaryResponseExample.json';
import { useCommentInlineLinkingStreaming } from '../data/useCommentInlineLinkingStreaming';

import { CommentThread } from './CommentThread';
import { CommentPanelCollapsibleSummary } from './CommentPanelCollapsibleSummary';
import { CommentReferenceLinkWrapper } from './CommentReferenceLinkWrapper';

const messages = defineMessages({
	summaryHeading: {
		id: 'comments-panel.summary.heading',
		defaultMessage: 'Summary',
		description: 'Heading for the comments summary section',
	},
});

type CommentPanelListProps = {
	commentThreads: CommentData[];
	supportedTopLevelActions: CommentAction[];
	flags?: FlagsStateContainer;
	onAllCommentsRendered?: () => void;
	onCommentBatchRendered?: () => void;
};

const listStyles = css({
	overflowY: 'auto',
	overflowX: 'hidden',
	flexGrow: 1,
	display: 'block',
});

const summaryContainerStyles = xcss({
	marginBottom: 'space.200',
	paddingLeft: 'space.200',
	paddingRight: 'space.200',
	paddingTop: 'space.0',
});

const summaryContentStyles = css({
	font: token('font.body'),
	textAlign: 'left',
	overflowWrap: 'break-word',
	whiteSpace: 'normal',
	direction: 'ltr',
	// Style bullet points to be grey
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Styling markdown content from markdown-to-jsx
	'& ul': {
		listStyle: 'disc',
		paddingLeft: '20px',
		margin: '0',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Styling markdown content from markdown-to-jsx
	'& li': {
		color: token('color.text.subtlest'),
		marginBottom: '8px',
	},
	// Style source links as blue rounded rectangles with bolded grey numbers
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Styling markdown content from markdown-to-jsx
	'& a[id="comment-source-link"]': {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: token('color.background.information'),
		color: token('color.text.subtle'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.semibold'),
		padding: '2px 6px',
		borderRadius: token('radius.small'),
		textDecoration: 'none',
		marginLeft: '4px',
		'&:hover': {
			backgroundColor: token('color.background.information.hovered'),
		},
	},
});

// this component holds a list of comments
export const CommentsPanelList = ({
	commentThreads,
	supportedTopLevelActions,
	flags,
	onAllCommentsRendered,
	onCommentBatchRendered,
}: CommentPanelListProps) => {
	const intl = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const pageMode = useGetPageMode();
	const pageClass = usePageClass();
	const { currentSort, currentlySelectedThreadKey, currentView } = useCommentsPanelState();
	const { setCurrentlyRenderedThreads } = useCommentsPanelActions();

	// state for collapsible summary, initially set to true
	const [isSummaryCollapsed, setIsSummaryCollapsed] = useState(true);
	const summaryContainerRef = useRef<HTMLDivElement>(null);

	const [summaryError, setSummaryError] = useState<Error | null>(null);
	const [isLoadingSummary, setIsLoadingSummary] = useState(true);

	const [openPopupId, setOpenPopupId] = useState<string | null>(null);

	//TODO match comments from the comment source linking
	// Get real comments from the page
	const { commentsDataMap } = useCommentsDataState();

	// Build array of real comment IDs from the page (in order)
	const realCommentIds = useMemo(() => {
		const allCommentIds: string[] = [];

		if (commentsDataMap?.inline) {
			Object.keys(commentsDataMap.inline)
				.sort()
				.forEach((threadKey) => {
					const comment = commentsDataMap.inline[threadKey];
					if (comment?.id) {
						allCommentIds.push(comment.id);
					}
				});
		}

		if (commentsDataMap?.general) {
			Object.keys(commentsDataMap.general)
				.sort()
				.forEach((threadKey) => {
					const comment = commentsDataMap.general[threadKey];
					if (comment?.id) {
						allCommentIds.push(comment.id);
					}
				});
		}
		return allCommentIds;
	}, [commentsDataMap]);

	const { inlineLinkingContent, updateBuffer, resetStreaming } = useCommentInlineLinkingStreaming(
		35,
		3,
	);

	const summaryData = exampleResponse as CommentSummaryResponse;

	const fullSummaryMarkdown = useMemo(() => {
		if (!fg('confluence_frontend_comments_panel_summary')) {
			return '';
		}
		try {
			return mapCommentSummaryResponseToMarkdown(summaryData);
		} catch (error) {
			return '';
		}
	}, [summaryData]);

	// Handle errors separately from useMemo to avoid setState in useMemo
	useEffect(() => {
		if (fg('confluence_frontend_comments_panel_summary')) {
			try {
				mapCommentSummaryResponseToMarkdown(summaryData);
			} catch (error) {
				setSummaryError(error as Error);
			}
		}
	}, [summaryData]);

	// Custom markdown overrides to render comment reference links with popups
	const markdownOverrides = useMemo(
		() => ({
			a: ({ children, href, ...props }: any) => {
				const commentId = props['data-comment-id'] || props.id;

				if (commentId && typeof commentId === 'string') {
					// Check if this is a mock reference format (comment_id_X)
					const mockFormatMatch = commentId.match(/comment_id_(\d+)/);

					let actualCommentId: string | null = null;

					if (mockFormatMatch) {
						// Mock format: map reference number to real comment ID
						const referenceNumber = parseInt(mockFormatMatch[1], 10);
						actualCommentId = realCommentIds[referenceNumber - 1] || null;
					} else {
						// Already a real comment ID (e.g., "5980218501") - use it directly
						actualCommentId = commentId;
					}

					// Render comment reference link if we have a valid comment ID
					if (actualCommentId) {
						return (
							<CommentReferenceLinkWrapper
								commentId={actualCommentId}
								openPopupId={openPopupId}
								onOpenChange={(isOpen, id) => {
									setOpenPopupId(isOpen ? id : null);
								}}
							>
								{children}
							</CommentReferenceLinkWrapper>
						);
					}
				}

				// Render regular link for non-comment links
				return (
					<Anchor href={href} {...props}>
						{children}
					</Anchor>
				);
			},
		}),
		[openPopupId, realCommentIds],
	);

	// Feed the content into the buffer for streaming
	useEffect(() => {
		if (fullSummaryMarkdown && fullSummaryMarkdown.length > 0) {
			resetStreaming();
			updateBuffer(fullSummaryMarkdown);
		}
	}, [fullSummaryMarkdown, updateBuffer, resetStreaming]);

	// Start loading when the summary feature is enabled
	useEffect(() => {
		if (fg('confluence_frontend_comments_panel_summary')) {
			setIsLoadingSummary(true);
		}
	}, []);

	const handleSummaryExpandCollapse = () => {
		setIsSummaryCollapsed(!isSummaryCollapsed);
	};

	const eventEmitter =
		pageMode === PageMode.EDIT || pageMode === PageMode.LIVE
			? getEditorAnnotationEventEmitter()
			: getRendererAnnotationEventEmitter();

	const sortedCommentThreads = useMemo(
		() => getSortedCommentThreads(currentSort, commentThreads),
		[commentThreads, currentSort],
	);

	// Whenever the list that we're rendering here changes, we want to update the list that the keyboard shortcuts are listening to
	useEffect(() => {
		setCurrentlyRenderedThreads(
			sortedCommentThreads.map((thread) => ({ ...thread.location, id: thread.id })),
		);

		return () => {
			// When this component is unmounted, we want to clear the list that the keyboard shortcuts are listening to
			setCurrentlyRenderedThreads([]);
		};
	}, [sortedCommentThreads, setCurrentlyRenderedThreads]);

	const actionsToShowUsers =
		pageMode === PageMode.EDIT
			? supportedTopLevelActions.filter((action) => action !== 'delete')
			: supportedTopLevelActions;

	// Props we want the batch renderer to pass through that aren't comment specific
	const passThroughProps = {
		// When sorting by MOST_RECENT, the comment type separator should never be rendered
		...(currentSort === SortValues.MOST_RECENT ? { shouldRenderCommentTypeSeparator: false } : {}),
		supportedTopLevelActions: actionsToShowUsers,
		eventEmitter,
		pageMode,
		flags,
		onLastThreadRendered: onAllCommentsRendered,
		onCommentBatchRendered,
	};

	const lastSelected = useRef(currentlySelectedThreadKey);
	const selectedInlineCommentParentThread = commentThreads.find(
		(thread) =>
			'inlineMarkerRef' in thread.location &&
			thread.location.inlineMarkerRef === currentlySelectedThreadKey,
	);
	const isActiveInlineComment =
		selectedInlineCommentParentThread &&
		!selectedInlineCommentParentThread.wasRemovedByAnotherUser &&
		'inlineResolveProperties' in selectedInlineCommentParentThread.location &&
		selectedInlineCommentParentThread.location.inlineResolveProperties?.resolved === false;

	if (lastSelected.current !== currentlySelectedThreadKey) {
		// The selected annotation has changed, so we need to update the last selected
		lastSelected.current = currentlySelectedThreadKey;

		if (currentlySelectedThreadKey && isActiveInlineComment) {
			// If the selected annotation has changed to a new one, we need to start the show comment experience
			selectInlineCommentCompoundExperience.showComment.start({
				pageClass,
				annotationId: currentlySelectedThreadKey,
				entryPoint: 'unknown:CommentsPanelList',
			});
		} else {
			// If an non inline comment or non active inline comment is selected,
			// we abort any running show comment experience
			selectInlineCommentCompoundExperience.showComment.abort({
				abortReason: 'Non inline comment being shown',
			});
		}
	}

	useEffect(() => {
		return () => {
			// If an inline comment is in the process of being shown as the sidebar closes,
			// we abort any running show comment experience.
			selectInlineCommentCompoundExperience.showComment.abort({
				abortReason: 'Comment sidebar dismissed',
			});
		};
	}, []);

	useEffect(() => {
		if (!isActiveInlineComment) {
			return;
		}

		const initialTaskId = experienceTracker.getExperienceState(
			SELECT_INLINE_COMMENT_EXPERIENCE,
		)?.taskId;

		setTimeout(() => {
			requestAnimationFrame(() => {
				const currentTaskId = experienceTracker.getExperienceState(
					SELECT_INLINE_COMMENT_EXPERIENCE,
				)?.taskId;

				if (!currentTaskId) {
					// There is no running experience, no need to abort.
					return;
				}

				if (initialTaskId !== currentTaskId) {
					// The experience has been restarted or has otherwise ended.
					return;
				}

				if (currentlySelectedThreadKey !== lastSelected.current) {
					return;
				}

				const selectedSidebarThread = document.querySelector(
					`[data-selected-comment-thread="${currentlySelectedThreadKey}"]`,
				);

				if (!selectedSidebarThread) {
					selectInlineCommentCompoundExperience.showComment.fail(
						new Error('No selected comment thread found'),
					);
				} else {
					const isVisible = selectedSidebarThread.checkVisibility();

					if (!isVisible) {
						selectInlineCommentCompoundExperience.showComment.fail(
							new Error('Selected comment thread is not visible'),
						);
					}

					selectInlineCommentCompoundExperience.showComment.complete(
						'Comment Sidebar inline comment selected',
					);
				}
			});
		}, 1000);
	}, [currentlySelectedThreadKey, isActiveInlineComment, experienceTracker]);

	return (
		<div
			id="comments-panel-list-container"
			data-testid="comments-panel-list-container"
			data-current-view={currentView}
			css={listStyles}
		>
			{/* Collapsible Summary Section */}
			{!summaryError && fg('confluence_frontend_comments_panel_summary') && (
				<Box xcss={summaryContainerStyles}>
					<CommentPanelCollapsibleSummary
						isCollapsed={isSummaryCollapsed}
						onExpandCollapse={handleSummaryExpandCollapse}
						ref={summaryContainerRef}
						isLoading={isLoadingSummary}
						title={
							<Heading as="h2" size="xlarge">
								<Text size="large" weight="bold">
									{intl.formatMessage(messages.summaryHeading)}
								</Text>
							</Heading>
						}
					>
						{inlineLinkingContent && (
							<div css={summaryContentStyles}>
								<Markdown options={{ overrides: markdownOverrides }}>
									{inlineLinkingContent}
								</Markdown>
							</div>
						)}
					</CommentPanelCollapsibleSummary>
				</Box>
			)}

			{/* Comments List */}
			<CommentBatchList
				commentsList={sortedCommentThreads}
				passThroughProps={passThroughProps}
				Component={CommentThread}
				getElementsToBatch={getCommentThreadsToBatch}
			/>
		</div>
	);
};
